import React, { useCallback, useRef } from 'react'
import { Col, Row } from 'react-bootstrap'
import { State, useHookstate } from '@hookstate/core'
import { Validation } from '@hookstate/validation'
import { Coupons, useExhibitorDataState } from '../../globalStates/ExhibitorDataState'
import { useLanguageState } from '../../globalStates/LanguageState'
import { getElementFieldValue, makeSetHandler, makeValidationHandler } from '../../globalStates/utils'
import { fieldNameByLang, getElementFieldValueWithLanguage, makeSetHandlerWithLanguage, makeValidationHandlerWithLanguage, useLanguageSwitch } from '../../utils/Language'
import Section from '../../components/Section'
import TextField from '../../components/Field/Text'
import TextAreaField from '../../components/Field/TextArea'
import FilePicker from '../../components/FilePicker'
import DateTimeField from '../../components/Field/DateTime'
import LanguageSwitch from '../languageSwitch'

interface CouponsSectionProps {
    couponsState: State<Coupons, Validation>
    index: number
    onClickRemove: (productId: string) => void
}

const CouponsSection: React.FC<CouponsSectionProps> = ({ couponsState, index, onClickRemove }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const coupons = useHookstate(couponsState)
    const isMonoLingual = branding.configuration.monoLingual ? 'De' : 'En'
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual);
    const sectionRef = useRef<HTMLDivElement>(null)

    const onChangeLogo = useCallback((image: File, onUploadProgress, onSuccessCallback, onErrorCallback) => {
        exiState.upload("LOGO", image, onUploadProgress)
            .then(response => {
                onSuccessCallback();
                if (response) {
                    coupons.logoFileId.set(response.fileId)
                    coupons.logoUrl.set(response.fileUrl)
                }
            })
            .catch(reason => onErrorCallback(reason))
    }, [exiState, coupons])

    const onRemoveLogo = useCallback(() => {
        coupons.logoFileId.set('')
        coupons.logoUrl.set('')
    }, [coupons])

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(coupons.id.get())
    }, [onClickRemove, coupons])

    return (
        <Section
            ref={sectionRef}
            title={coupons.name.get() || branding.configuration.couponsPage.sctCoupons.nameEmptyCoupons}
            collapsible
            onClickRemove={onClickRemoveHandler}
            entityState={coupons}>
            <Row>
                <Col xs={3}>
                    <FilePicker
                        name="logoUrl"
                        label={branding.configuration.couponsPage.sctCoupons.lblCouponsImage}
                        message={branding.configuration.couponsPage.sctCoupons.lblCouponsImageMessage}
                        browseLabel={branding.configuration.couponsPage.sctCoupons.btnBrowseFiles}
                        deleteLabel={branding.configuration.couponsPage.sctCoupons.btnDeleteImg}
                        placeholderTitle={branding.configuration.couponsPage.sctCoupons.hntPlaceholderTitle}
                        placeholderText={branding.configuration.couponsPage.sctCoupons.hntPlaceholderProp}
                        value={coupons.logoUrl.get()}
                        onChangeImage={onChangeLogo}
                        onRemoveImage={onRemoveLogo}
                    />
                </Col>
                <Col xs={9}>
                    <Row>
                        <Col>
                            <LanguageSwitch
                                id={`coupons_${index}_language_switch`}
                                name={`coupons_${index}_languages`}
                                value={currentLanguage}
                                onChange={onChangeLanguageHandler}
                                entityState={couponsState}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                name="name"
                                label={branding.configuration.couponsPage.sctCoupons.lblCouponsName}
                                placeholder={branding.configuration.couponsPage.sctCoupons.hntCouponsName}
                                validate={makeValidationHandler(coupons, 'name')}
                                value={getElementFieldValue(coupons, "name")}
                                onChange={makeSetHandler(coupons, 'name')}
                                required
                            />
                        </Col>
                        <Col>
                            <TextField
                                name={fieldNameByLang("url", currentLanguage)}
                                label={branding.configuration.couponsPage.sctCoupons.lblURL}
                                placeholder={branding.configuration.couponsPage.sctCoupons.hntURL}
                                validate={makeValidationHandlerWithLanguage(coupons, 'url', currentLanguage)}
                                value={getElementFieldValueWithLanguage(coupons, "url", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(coupons, 'url', currentLanguage)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <DateTimeField
                                name="validStart"
                                label={branding.configuration.couponsPage.sctCoupons.lblCouponsValidStart}
                                placeholder={branding.configuration.couponsPage.sctCoupons.hntCouponsValidStart}
                                validate={makeValidationHandler(coupons, 'validStart')}
                                value={coupons.validStart.get()}
                                onChange={(datetime: Date) => {
                                    coupons.validStart.set(datetime)
                                }}
                                required
                            />
                        </Col>
                        <Col>
                            <DateTimeField
                                name="validEnd"
                                label={branding.configuration.couponsPage.sctCoupons.lblCouponsValidEnd}
                                placeholder={branding.configuration.couponsPage.sctCoupons.hntCouponsValidEnd}
                                validate={makeValidationHandler(coupons, 'validEnd')}
                                value={coupons.validEnd.get()}
                                onChange={(datetime: Date) => {
                                    coupons.validEnd.set(datetime)
                                }}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("descriptionTitle", currentLanguage)}
                                label={branding.configuration.couponsPage.sctCoupons.lblDescriptionTitle}
                                placeholder={branding.configuration.couponsPage.sctCoupons.hntDescriptionTitle}
                                value={getElementFieldValueWithLanguage(coupons, "descriptionTitle", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(coupons, 'descriptionTitle', currentLanguage)}
                                limit={200}
                                style={{ height: '80px' }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("descriptionLong", currentLanguage)}
                                label={branding.configuration.couponsPage.sctCoupons.lblDescriptionLong}
                                placeholder={branding.configuration.couponsPage.sctCoupons.hntDescriptionLong}
                                value={getElementFieldValueWithLanguage(coupons, "descriptionLong", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(coupons, 'descriptionLong', currentLanguage)}
                                limit={600}
                                style={{ height: '80px' }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("descriptionShort", currentLanguage)}
                                label={branding.configuration.couponsPage.sctCoupons.lblDescriptionShort}
                                placeholder={branding.configuration.couponsPage.sctCoupons.hntDescriptionShort}
                                value={getElementFieldValueWithLanguage(coupons, "descriptionShort", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(coupons, 'descriptionShort', currentLanguage)}
                                limit={300}
                                style={{ height: '80px' }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Section>
    );
}

export default React.memo(CouponsSection)
